'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { PageContext } from './context/page-context';
import { pageHeaderRecipe } from './styles/page-header.styles';
import type { PageHeaderPropsInterface } from './types/page-header.types';

/**
 * The page title is used for the primary heading of the page, typically an `<h1>`.
 *
 * For cross-page consistency, the heading style is fixed, but you can still change the underlying
 * element with the `level` prop on `Heading`. If for some reason you need a different style,
 * you can simply use a `Heading` with the generic `PageContent` component.
 *
 * @since 10.0.0
 */
export const PageHeader = forwardRef<HTMLDivElement, PageHeaderPropsInterface>(
  (props: PageHeaderPropsInterface, forwardedRef) => {
    const { asChild = false, children, className, css: cssProp, fluid, ...rest } = props;
    const context = useContext(PageContext);

    const styles = css(pageHeaderRecipe.raw({ fluid: fluid ?? context.fluid }), cssProp);

    const Component = asChild ? Slot : 'div';

    return (
      <Component
        data-c11n-component="Page.Header"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_page__header', styles, className)}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

PageHeader.displayName = 'Page.Header';
